import React, { PureComponent } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import Checkbox from '@active/react-ui/Checkbox';
import CheckboxGroup from '@active/react-ui/CheckboxGroup';
import Form, { FormConsumer } from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import FormLabel from '@active/react-ui/FormLabel';
import Input from '@active/react-ui/Input';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import Locale from '@active/react-utils/i18n/Locale';

import { newCreditCardType } from '../types';
import { filterNumbers, enableCVV, executeCaptcha } from '../utils/common';
import Images from '../imgs/ccImgs/imgMap';
import {
  cardNumberImgShow, getCursorPosition, setCursorPosition, DELETE, formatCardNumber,
} from './cardNumberHandler';
import {
  cardNumberValidator, holderNameValidator, getCardCurrentType,
} from '../utils/validators';
import encrypt from '../utils/encrypt';
import getCardType from '../utils/getCardType';

import CVVField from '../components/CVVField';
import DateSelect from '../components/DateSelect';
import './NewCreditCard.scss';

export class NewCreditCard extends PureComponent {
  static propTypes = newCreditCardType

  static defaultProps = {
    availableCardTypes: null,
    fields: {},
    amsConfig: {},
    forceSaveInWallet: false,
    displayCVVHelpTextAfterLabel: false,
    hideCVVHelpText: false,
  }

  constructor(props) {
    super(props);
    const {
      availableCardTypes,
      triggers: { commitTrigger, validateTrigger = noop },
      fields,
      forceSaveInWallet,
      isSavable,
    } = props;
    const { cardNumber } = fields;
    const preCardNumber = cardNumber ? formatCardNumber(cardNumber) : '';
    this.state = {
      currentCardType: 'INVALID',
      fields: {
        ...fields,
        isSaved: isSavable && forceSaveInWallet ? [ 'true' ] : [],
        date: {
          month: '',
          year: '',
        },
        cardNumber: preCardNumber,
      },
      originCardNumber: preCardNumber,
      originSpace: 0,
    };

    if (availableCardTypes && availableCardTypes.length) {
      commitTrigger(this.committer);
      validateTrigger((callback) => {
        this.form.validateForm({ shouldTouchFields: true }).then(
          (res) => {
            callback(isEmpty(res));
          },
        );
      });
    }
  }

  showCardNumberIcon = (type) => {
    this.setState({
      cardArts: cardNumberImgShow(type),
      currentCardType: type,
    });
  }

  componentDidMount() {
    const { fields: { cardNumber } } = this.state;
    const { availableCardTypes } = this.props;
    if (availableCardTypes && availableCardTypes.length && cardNumber) {
      const type = getCardCurrentType(availableCardTypes, cardNumber);
      this.showCardNumberIcon(type);
    }
  }

  committer = (triggerParams, committerCallback) => {
    const { address, email, phoneNumber } = triggerParams || {};
    const {
      country, line1, line2, city, stateProvince, postalCode,
    } = address || {};
    this.setState({
      committerInput: {
        Country: country,
        Address: `${line1} ${line2}`,
        City: city,
        State: stateProvince,
        postalCode,
        Email: email,
        BillingPhoneNumber: phoneNumber,
      },
      committerCallback,
    }, () => {
      this.form.validateForm({ shouldTouchFields: true }).then(
        (res) => {
          if (isEmpty(res)) {
            this.form.submitForm();
          } else {
            committerCallback({ errorCode: 'INVALID_PARAMS' });
          }
        },
      );
    });
  }

  handleCreateAmsAccount = ({ params, successCallbackParams }) => {
    const {
      services: { createAmsAccount },
    } = this.props;
    const { committerCallback } = this.state;

    createAmsAccount({
      params,
      resolve: (resp) => {
        if (resp === 'SUCCESS') {
          committerCallback(successCallbackParams);
        } else if (resp === 'InvalidCaptcha') {
          committerCallback({ errorCode: 'INVALID_CAPTCHA' });
        } else {
          committerCallback({ errorCode: 'ERROR_SAVE_CARD_FAILED' });
        }
      },
      reject: () => {
        committerCallback({ errorCode: 'ERROR_SAVE_CARD_FAILED' });
      },
    });
  }

  handleSubmit = ({
    holderName, cardNumber, cvv,
    isSaved, expiryDate: { month, year },
  }) => {
    const {
      amsConfig: {
        modulus, sessionId, exponent, enableCaptcha, encryptionKeyAlias,
      },
      isSavable,
    } = this.props;
    const {
      committerInput: {
        Country, Address, City, State, postalCode, Email, BillingPhoneNumber,
      },
      committerCallback,
    } = this.state;
    const savedInWallet = !!(isSaved && isSaved[0] === 'true');
    const cipherText = encrypt({
      modulus, exponent, holderName, cardNumber: filterNumbers(cardNumber), month, year, cvv, postalCode,
    });
    const successCallbackParams = { sessionId, ...(isSavable ? { savedInWallet } : {}) };
    const params = {
      SessionID: sessionId,
      cipherText,
      modulus,
      Country,
      State,
      City,
      Address,
      Email,
      BillingPhoneNumber,
      EncryptionKeyAlias: encryptionKeyAlias,
    };
    if (enableCaptcha) {
      executeCaptcha(this.handleCreateAmsAccount, params, successCallbackParams, committerCallback);
    } else {
      this.handleCreateAmsAccount({ params, successCallbackParams });
    }
  }

  cardNumberValidate = (value) => {
    const { i18n, availableCardTypes } = this.props;
    return cardNumberValidator(
      availableCardTypes,
      i18n,
      this.showCardNumberIcon,
      value,
    );
  };

  holderNameValidate = (value) => {
    const { i18n } = this.props;
    return holderNameValidator(i18n, value);
  };

  handleCardHolderNameKeyup = (event) => {
    const { target, target: { value } } = event;
    target.value = value.split('')
      .filter(s => s.charCodeAt(0) >= 0 && s.charCodeAt(0) <= 127).join('');
    this.form.setFieldValue('holderName', target.value);
  }

  handleKeyDown = (e) => {
    const { target } = e;
    const elem = target;
    const currentPos = getCursorPosition(elem);
    // left space number
    const curSp = (elem.value.slice(0, currentPos).match(/\s/g) || []).length;
    this.setState({ originSpace: curSp });
  }

  handleKeyUp = (e) => {
    const { target, which } = e;
    const elem = target;
    // for some andriod system keyboard can not input (eg: vivo)
    setTimeout(() => {
      const { originCardNumber, originSpace } = this.state;
      const str = elem.value;
      const currentPos = getCursorPosition(elem);
      if (str === formatCardNumber(originCardNumber)) { return; }
      elem.value = formatCardNumber(elem.value);
      this.form.setFieldValue('cardNumber', elem.value);
      // left space number
      const curSp = (elem.value.slice(0, currentPos).match(/\s/g) || []).length;
      // If the value is reset, the cursor position will be set to
      // the last position by default, so we should reset the cusor position
      this.setState({ originCardNumber: elem.value }, () => {
        if (which === DELETE) {
          setCursorPosition(elem, currentPos);
        } else {
          setCursorPosition(elem, currentPos + (curSp - originSpace));
        }
      });
    }, 0);
  };

  render() {
    const {
      availableCardTypes, isSavable,
      i18n, collectCVVConfigs, isCVVAvailable,
      forceSaveInWallet, displayCVVHelpTextAfterLabel, hideCVVHelpText,
      horizontal = true,
    } = this.props;
    const { cardArts = [], currentCardType, fields } = this.state;
    if (!availableCardTypes || !availableCardTypes.length) {
      return null;
    }

    const isCVVEnable = enableCVV(currentCardType, collectCVVConfigs, isCVVAvailable);
    return (
      <div className="checkout__new-cc checkout-cc">
        <div className="checkout__new-cc__cardtypes">
          <Locale id="checkout.lib.components.newCreditCard.label.cardtypes" />
          <div className="checkout__new-cc__cardtypes-img">
            {
              availableCardTypes.map(type => (Array.isArray(Images[type]) ? Images[type] : [ Images[type] ]).map(
                (image, idx) => <img
                  key={idx}
                  src={image}
                  className={`checkout__new-cc__logo-${type}`}
                  alt={
                    i18n(
                      `checkout.lib.components.payment.credit.cart.type.${getCardType(type, image)}`,
                    )
                  }/>,
              ),
              )
            }
          </div>
        </div>
        <Form
          onSubmit={this.handleSubmit}
          horizontal={horizontal}
          initialValues={fields}>
          <div className="checkout__new-cc__holderName">
            <FormGroup name="holderName" required>
              <FormLabel>
                <Locale id="checkout.lib.components.common.holder.name"/>
              </FormLabel>
              <FormField
                as={Input}
                type="text"
                autoComplete="cc-name"
                maxlength="50"
                onKeyUp={this.handleCardHolderNameKeyup}
                validate={this.holderNameValidate}/>
            </FormGroup>
          </div>
          <div className="checkout__new-cc__cardNumber">
            {/* type="tel" is to arouse number keyboard on mobile device. */}
            <FormGroup name="cardNumber" required>
              <FormLabel>
                <Locale id="checkout.lib.components.common.card.number"/>
              </FormLabel>
              <FormField
                as={Input}
                autoComplete="cc-number"
                type="tel"
                maxlength="50"
                validate={ this.cardNumberValidate}
                onKeyUp={this.handleKeyUp}
                onKeyDown={this.handleKeyDown}
                lite
                postAddon={(<div className="checkout__new-cc__cardNumber-img">
                  {cardArts.map(({ image, cardType }, idx) => <img key={idx} alt={cardType} src={image} className="checkout__new-cc__cardNumber-imgIcon" />,
                  )}
                </div>)}/>
            </FormGroup>
          </div>
          <div className="checkout__new-cc__expiryDate">
            <FormGroup name="expiryDate" required>
              <FormLabel >
                <Locale id="checkout.lib.components.common.expiration.date" />
              </FormLabel>
              <FormField
                maxHeight="120px"
                as={DateSelect}/>
            </FormGroup>
          </div>
          <CVVField
            i18n={i18n}
            displayCVVHelpTextAfterLabel={displayCVVHelpTextAfterLabel}
            hideCVVHelpText={hideCVVHelpText}
            isCVVEnable={isCVVEnable}/>
          {isSavable
            && <FormGroup name="isSaved">
              <FormLabel/>
              <FormField as={CheckboxGroup}>
                <Checkbox className="checkout__save__payment__account__in__wallet"
                  name="savedInWallet"
                  value="true"
                  tabIndex="0"
                  disabled={forceSaveInWallet}>
                  <Locale id="checkout.lib.components.newCreditCard.checkbox.savedInWallet" />
                </Checkbox>
              </FormField>
            </FormGroup>
          }
          <FormConsumer>
            {
              ({
                form,
              }) => {
                this.form = form;
              }
            }
          </FormConsumer>
        </Form>
      </div>
    );
  }
}

export default withI18n(NewCreditCard);
